import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { AssistantMessage, Message } from '@ag-ui/core';
import { InkAiThinkingIndicator } from '@movable/ui';
import { Box } from '@mui/material';

import { MessageItem } from '@/components/shared/AgentAssistantPopup/MessageItem';
import { threadFadeIn } from '@/components/shared/AgentAssistantPopup/styles';

export type MessageListProps = {
  messages: Message[];
  isRunning: boolean;
  onThumbsUp?: (message: AssistantMessage) => void;
  onThumbsDown?: (
    message: AssistantMessage,
    values: {
      issueType: string;
      comment: string;
    },
  ) => void;

  onCopy?: (content: string, messageIds: string[]) => void;
  /**
   * Space kept above the latest user message when it scrolls to the top. Keep it
   * smaller than the list's message gap or the previous message peeks in above it.
   */
  userMessageTopOffset?: number;
  /**
   * Label for the thinking indicator while a run is in progress (e.g. the
   * backend's current activity). Unset keeps the indicator's default label.
   */
  thinkingLabel?: string;
};

const DEFAULT_USER_MESSAGE_TOP_OFFSET = 24;
const SCROLL_THRESHOLD = 64;

export const MessageList = ({
  messages,
  isRunning,
  onThumbsUp,
  onThumbsDown,
  onCopy,
  userMessageTopOffset = DEFAULT_USER_MESSAGE_TOP_OFFSET,
  thinkingLabel,
}: MessageListProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement | null>(null);
  const previousScrollTopRef = useRef(0);
  const shouldAutoScrollRef = useRef(true);
  const programmaticScrollTargetRef = useRef<number | null>(null);
  const initialIdsRef = useRef<Set<string> | null>(null);
  const hasMountedRef = useRef(false);
  const [latestUserMessage, setLatestUserMessage] =
    useState<HTMLDivElement | null>(null);
  if (initialIdsRef.current === null) {
    initialIdsRef.current =
      messages.length > 1
        ? new Set(messages.map((message) => message.id))
        : new Set<string>();
  }

  const initialIds = initialIdsRef.current;
  const lastUserIndex = useMemo(
    () =>
      messages.reduce(
        (lastIndex, message, index) =>
          message.role === 'user' ? index : lastIndex,
        -1,
      ),
    [messages],
  );

  const getLatestUserScrollTop = useCallback(() => {
    if (!latestUserMessage) {
      return 0;
    }

    return Math.max(0, latestUserMessage.offsetTop - userMessageTopOffset);
  }, [latestUserMessage, userMessageTopOffset]);

  const getContentScrollTop = useCallback(() => {
    const el = scrollRef.current;

    if (!el) {
      return 0;
    }

    const currentSpacerHeight = spacerRef.current?.offsetHeight ?? 0;
    return Math.max(0, el.scrollHeight - currentSpacerHeight - el.clientHeight);
  }, []);
  const getMaxAllowedScrollTop = useCallback(() => {
    return Math.max(getLatestUserScrollTop(), getContentScrollTop());
  }, [getLatestUserScrollTop, getContentScrollTop]);

  const handleScroll = () => {
    const el = scrollRef.current;

    if (!el) {
      return;
    }

    const currentScrollTop = el.scrollTop;
    const programmaticTarget = programmaticScrollTargetRef.current;

    if (programmaticTarget !== null) {
      const previousScrollTop = previousScrollTopRef.current;
      const expectedDirection = Math.sign(
        programmaticTarget - previousScrollTop,
      );
      const actualDirection = Math.sign(currentScrollTop - previousScrollTop);

      const reachedTarget =
        Math.abs(programmaticTarget - currentScrollTop) <= SCROLL_THRESHOLD;

      const userMovedAgainstTarget =
        expectedDirection !== 0 &&
        actualDirection !== 0 &&
        expectedDirection !== actualDirection;

      if (userMovedAgainstTarget) {
        shouldAutoScrollRef.current = false;
        programmaticScrollTargetRef.current = null;
      } else if (reachedTarget) {
        programmaticScrollTargetRef.current = null;
      }

      previousScrollTopRef.current = currentScrollTop;
      return;
    }

    const contentScrollTop = getContentScrollTop();

    shouldAutoScrollRef.current =
      contentScrollTop - el.scrollTop <= SCROLL_THRESHOLD;

    previousScrollTopRef.current = el.scrollTop;
  };
  const setLatestUserMessageRef = useCallback((node: HTMLDivElement | null) => {
    setLatestUserMessage(node);
  }, []);

  // The spacer only needs to be as tall as it takes to let the latest user
  // message scroll to the top. As the reply grows it shrinks to 0, so there is
  // no blank space left below the last message.
  const syncSpacerHeight = useCallback(() => {
    const el = scrollRef.current;

    if (!el || !latestUserMessage) {
      return null;
    }

    const currentSpacerHeight = spacerRef.current?.offsetHeight ?? 0;
    const contentHeightWithoutSpacer = el.scrollHeight - currentSpacerHeight;

    const targetScrollTop = Math.max(
      0,
      latestUserMessage.offsetTop - userMessageTopOffset,
    );

    const requiredSpacerHeight = Math.max(
      0,
      targetScrollTop + el.clientHeight - contentHeightWithoutSpacer,
    );

    if (Math.abs(requiredSpacerHeight - currentSpacerHeight) > 1) {
      if (spacerRef.current) {
        spacerRef.current.style.height = `${requiredSpacerHeight}px`;
      }
    }

    return targetScrollTop;
  }, [latestUserMessage, userMessageTopOffset]);

  // Re-sync the spacer on every streaming update. Unlike the effect below, this
  // never scrolls; that one runs only when a new user message is sent.
  useLayoutEffect(() => {
    syncSpacerHeight();
  }, [messages, isRunning, syncSpacerHeight]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    const targetScrollTop = syncSpacerHeight();

    if (!el || targetScrollTop === null) {
      return;
    }

    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    shouldAutoScrollRef.current = true;
    programmaticScrollTargetRef.current = targetScrollTop;
    previousScrollTopRef.current = el.scrollTop;

    el.scrollTo({
      top: targetScrollTop,
      behavior: 'smooth',
    });
  }, [syncSpacerHeight]);

  useLayoutEffect(() => {
    if (
      !isRunning ||
      !shouldAutoScrollRef.current ||
      lastUserIndex === -1 ||
      !messages
        .slice(lastUserIndex + 1)
        .some(
          (message) =>
            message.role === 'assistant' &&
            ((message as AssistantMessage).content ?? '').trim() !== '',
        )
    ) {
      return;
    }

    const el = scrollRef.current;

    if (!el) {
      return;
    }

    const targetScrollTop = getMaxAllowedScrollTop();
    programmaticScrollTargetRef.current = targetScrollTop;
    previousScrollTopRef.current = el.scrollTop;

    const rafId = requestAnimationFrame(() => {
      const currentEl = scrollRef.current;

      if (!currentEl) {
        return;
      }

      if (currentEl.scrollTop < targetScrollTop) {
        currentEl.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth',
        });
      } else {
        programmaticScrollTargetRef.current = null;
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [messages, isRunning, lastUserIndex, getMaxAllowedScrollTop]);

  const assistantMessageIdsById = useMemo(() => {
    const result = new Map<string, string[]>();
    let currentTurnIds: string[] = [];
    const turns: string[][] = [];
    messages.forEach((message) => {
      if (message.role === 'user') {
        if (currentTurnIds.length) {
          turns.push(currentTurnIds);
        }
        currentTurnIds = [];
        return;
      }

      // Empty assistant messages render nothing, so they must not count as the
      // "last message in turn" or the visible one would lose its feedback actions.
      if (
        message.role === 'assistant' &&
        ((message as AssistantMessage).content ?? '').trim() !== ''
      ) {
        currentTurnIds.push(message.id);
      }
    });

    if (currentTurnIds.length) {
      turns.push(currentTurnIds);
    }

    turns.forEach((turnIds) => {
      turnIds.forEach((id) => {
        result.set(id, turnIds);
      });
    });

    return result;
  }, [messages]);
  const showThinkingTail = useMemo(() => {
    const last = messages[messages.length - 1];

    const lastIsEmptyAssistant =
      last?.role === 'assistant' &&
      ((last as AssistantMessage).content ?? '').trim() === '';

    // Text followed by tool calls ("Let me check...") leaves the tools running
    // after the last message, with no result message yet to trigger the tail.
    const lastAwaitsToolResults =
      last?.role === 'assistant' &&
      ((last as AssistantMessage).toolCalls?.length ?? 0) > 0;

    return (
      isRunning &&
      (last?.role === 'user' ||
        last?.role === 'tool' ||
        last?.role === 'reasoning' ||
        lastIsEmptyAssistant ||
        lastAwaitsToolResults)
    );
  }, [isRunning, messages]);

  return (
    // Scroll container for positioning new user messages and following AI responses.
    <Box
      ref={scrollRef}
      onScroll={handleScroll}
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        px: 2,
        py: 2,
        backgroundColor: 'background.paper',
        animation: `${threadFadeIn} 240ms cubic-bezier(0.22, 1, 0.36, 1) both`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      {messages.map((message, index) => {
        const isLatestUserMessage =
          message.role === 'user' && index === lastUserIndex;

        return (
          <Box
            key={message.id}
            ref={isLatestUserMessage ? setLatestUserMessageRef : undefined}
            // Messages that render nothing (tool, empty assistant) would still
            // add a flex `gap` each, creating blank bands between chunks.
            sx={{ '&:empty': { display: 'none' } }}
          >
            <MessageItem
              message={message}
              messages={messages}
              isRunning={isRunning}
              isEntering={!initialIds.has(message.id)}
              isCurrentTurn={index > lastUserIndex}
              onThumbsUp={onThumbsUp}
              onThumbsDown={onThumbsDown}
              onCopy={onCopy}
              messageIds={
                message.role === 'assistant'
                  ? assistantMessageIdsById.get(message.id)
                  : undefined
              }
            />
          </Box>
        );
      })}
      {showThinkingTail ? (
        <InkAiThinkingIndicator label={thinkingLabel} />
      ) : null}
      <Box
        ref={spacerRef}
        aria-hidden
        sx={{
          flexShrink: 0,
          height: 0,
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
};
